/**
 * Admin Dashboard Core Module
 * Central hub: stores config (5min refresh interval, performance thresholds), state (visitors/activities),
 * request queue (maxConcurrent 4, backoff 800ms exp, urgent/normal priority), utilities (format/categorize),
 * and Web Vitals tracking (LCP/FID/CLS/TTFB). Initializes admin-dashboard-api with retry logic.
 * All other modules (api/charts/ui) reference core.state and core.config.
 */

(function () {
  'use strict';

  // Conditional logging setup
  const isDev =
    window.location.hostname === 'localhost' || window.location.hostname.includes('.dev');
  const debug = isDev ? console.debug.bind(console) : () => {};
  const warn = isDev ? console.warn.bind(console) : () => {};
  const error = isDev ? console.error.bind(console) : () => {};

  /**
   * TimeoutError — thrown when a fetch exceeds REQUEST_TIMEOUT_MS.
   * Distinct from generic Error so the UI can show a targeted "Request timed out"
   * message instead of a generic failure banner.
   */
  class TimeoutError extends Error {
    constructor(message) {
      super(message);
      this.name = 'TimeoutError';
    }
  }
  window.AdminDashboardTimeoutError = TimeoutError;

  window.AdminDashboardCore = {
    // Configuration
    config: {
      refreshInterval: 300000, // 5 minutes (disabled by default, only used if manually enabled)
      idleTimeout: 1800000, // 30 minutes of inactivity before full refresh
      activityMaxItems: 20,
      // Production Vercel deployment (complete feature set with all analytics)
      apiProxyUrl: 'https://coracrea-website.vercel.app/api/shopify-proxy',

      // Slow-connection reliability: configurable per-request timeout (ms).
      // Applies to every queuedFetch call. Default 15 s balances slow connections
      // against hanging forever. Set to 0 to disable (not recommended).
      REQUEST_TIMEOUT_MS: 15000,

      // Extended timeouts used when Network Information API reports degraded connections.
      // 30 s for 3g, 60 s for 2g/slow-2g gives the server more time to respond on
      // very constrained mobile connections without hanging forever.
      REQUEST_TIMEOUT_3G_MS: 30000,
      REQUEST_TIMEOUT_SLOW_MS: 60000,

      // Concurrency caps — how many requests may be in-flight simultaneously.
      // On slow connections (2g/slow-2g) head-of-line blocking on a single slow pipe
      // is the dominant failure mode; capping to 2 prevents all slots being occupied
      // by non-critical requests and starving critical ones.
      MAX_CONCURRENT_NORMAL: 4,
      MAX_CONCURRENT_SLOW: 2,

      // How many consecutive TimeoutErrors trigger connection-problem mode.
      CONNECTION_HEALTH_THRESHOLD: 3,

      // Performance Metrics Constants
      performanceMetrics: {
        labels: {
          excellent: 'Excellent',
          good: 'Good',
          average: 'Average',
          needsImprovement: 'Needs Improvement',
          needsWork: 'Needs work',
          poor: 'Poor',
        },
        lcp: {
          thresholds: { good: 2500, needsImprovement: 4000 },
          industryAvg: '2.5s',
          unit: 's',
          divisor: 1000,
        },
        fid: {
          thresholds: { good: 100, needsImprovement: 300 },
          industryAvg: '100ms',
          unit: 'ms',
          divisor: 1,
        },
        cls: {
          thresholds: { good: 0.1, needsImprovement: 0.25 },
          industryAvg: '0.1',
          unit: '',
          divisor: 1,
        },
        ttfb: {
          thresholds: { good: 800, needsImprovement: 1800 },
          industryAvg: '800ms',
          unit: 'ms',
          divisor: 1,
        },
        bounceRate: {
          thresholds: { excellent: 50, good: 60, average: 70 },
          industryAvg: '50-60%',
        },
        pagesPerSession: {
          thresholds: { excellent: 2.5, good: 2.0, average: 1.5 },
          industryAvg: '2.0-2.5',
        },
        sessionDuration: {
          thresholds: { excellent: 150, good: 120, average: 60 },
          industryAvg: '2-3 min',
        },
      },
    },

    // ---------------------------------------------------------------------------
    // Centralized Timer Registry
    // All setInterval / setTimeout IDs that outlive a single function call are
    // registered here so clearAllTimers() can sweep them in one shot.
    //
    // registerTimer(id, type) — record a timer; returns id for chaining.
    //   type: 'interval' | 'timeout' (default 'timeout')
    // clearAllTimers()        — cancel every registered timer and empty the map.
    //   Called automatically alongside removeAllListeners() on beforeunload
    //   and visibilitychange(hidden).
    // ---------------------------------------------------------------------------
    _timerRegistry: new Map(), // Map<id, 'interval'|'timeout'>

    registerTimer(id, type = 'timeout') {
      if (id != null) this._timerRegistry.set(id, type);
      return id;
    },

    clearAllTimers() {
      this._timerRegistry.forEach((type, id) => {
        if (type === 'interval') clearInterval(id);
        else clearTimeout(id);
      });
      this._timerRegistry.clear();
      // Reset the idleTimer reference in state so cleanupIdleDetection doesn't
      // double-clear after clearAllTimers already did it.
      this.state.idleTimer = null;
    },

    // ---------------------------------------------------------------------------
    // Centralized Event Listener Registry
    // All long-lived addEventListener calls that outlive a single function are
    // registered here so removeAllListeners() can tear them all down in one shot.
    //
    // registerListener(target, event, handler, options?)
    //   — records {target, event, handler, options}; returns handler for chaining.
    // removeAllListeners()
    //   — calls removeEventListener for every registered entry and clears the list.
    //   Called automatically alongside clearAllTimers() on beforeunload and
    //   visibilitychange(hidden).
    // ---------------------------------------------------------------------------
    _listenerRegistry: [], // Array<{target, event, handler, options}>

    // Callbacks run when the tab becomes visible again (see setupPageLifecycleCleanup).
    _resumeHandlers: [], // Array<function>
    _lifecyclePaused: false, // True while the tab is hidden

    registerListener(target, event, handler, options) {
      this._listenerRegistry.push({ target, event, handler, options });
      return handler;
    },

    removeAllListeners() {
      this._listenerRegistry.forEach(({ target, event, handler, options }) => {
        target.removeEventListener(event, handler, options);
      });
      this._listenerRegistry = [];
    },

    // State
    state: {
      visitors: [],
      activities: [],
      charts: {},
      dailyRevenue: null, // Store today's revenue to prevent fluctuation
      dailyOrders: null,
      isFirstLoad: true,
      lastDataFetchTimestamp: null, // Track when data was last fetched for staleness check
      dataLoadInFlight: false, // True while AdminDashboard.loadData() is running; suppresses duplicate recovery loads
      liveVisitorBots: 0, // Store bot count from live-visitors API for bot chart
      lastActivityCount: 0, // Track previous order count to detect new orders
      drawerClosing: false, // Flag to prevent click-through when closing drawer
      tooltipHideTimer: null, // Timer for scheduled tooltip hide
      idleTimer: null, // Timer for idle detection
      lastActivityTime: Date.now(), // Track last user activity
      idleListenersAdded: false, // Prevent duplicate idle listener registration
      resetIdleTimerHandler: null, // Stored handler for cleanup
      tabsLoaded: {}, // Track which tabs have been lazy-loaded
      recentOrdersRequestId: 0, // Monotonic counter to detect stale period responses
      campaignRevenueRequestId: 0, // Monotonic counter to detect stale campaign period responses
      trafficChartRequestId: 0, // Monotonic counter for traffic chart stale-response guard
      revenueChartRequestId: 0, // Monotonic counter for revenue chart stale-response guard
      webVitals: {
        lcp: null,
        fid: null,
        cls: null,
        ttfb: null,
      },
      // Progressive loading progress tracker.
      // loadingProgress.loaded counts sections that have returned data (success or error).
      // loadingProgress.total is set at load start so the progress indicator has a denominator.
      // loadingProgress.phase is 'critical' until critical sections complete, then 'supplementary'.
      loadingProgress: {
        loaded: 0,
        total: 0,
        phase: 'idle', // 'idle' | 'critical' | 'supplementary' | 'done'
      },

      // ---------------------------------------------------------------------------
      // Connection health tracker.
      // consecutiveTimeouts counts uninterrupted TimeoutError responses.
      // When it reaches CONNECTION_HEALTH_THRESHOLD the dashboard switches to
      // manual-refresh-only mode (autoRefreshPaused=true, connectionProblem=true).
      // Any successful request resets both counters via resetConnectionHealth().
      // isOffline is set true when the offline event fires and cleared on online.
      // ---------------------------------------------------------------------------
      connectionHealth: {
        consecutiveTimeouts: 0, // incremented on each TimeoutError
        connectionProblem: false, // true after 3+ consecutive timeouts
        autoRefreshPaused: false, // true when polling is suspended (offline or problem)
        isOffline: false, // mirrors !navigator.onLine at runtime
        slowConnection: false, // true when Network Info API reports 2g or slow-2g
      },
    },

    // ---------------------------------------------------------------------------
    // Stale-while-revalidate helpers (sessionStorage).
    // Cache key format: "admin-dash-swr:<endpointKey>"
    // Entries: { data: any, ts: number (ms epoch) }
    // ---------------------------------------------------------------------------

    /** Maximum age (ms) at which a sessionStorage entry is considered fresh enough to show immediately. */
    SWR_FRESH_TTL_MS: 5 * 60 * 1000, // 5 minutes

    /**
     * Read a cached entry from sessionStorage for the given key.
     * Returns { data, ts, isStale } or null if absent / unparseable.
     *
     * @param {string} key - logical key (e.g. 'today-stats')
     * @returns {{ data: any, ts: number, isStale: boolean } | null}
     */
    swrRead(key) {
      try {
        const raw = sessionStorage.getItem('admin-dash-swr:' + key);
        if (!raw) return null;
        const entry = JSON.parse(raw);
        if (!entry || entry.data === undefined || !entry.ts) return null;
        const age = Date.now() - entry.ts;
        return { data: entry.data, ts: entry.ts, isStale: age > this.SWR_FRESH_TTL_MS };
      } catch (_e) {
        return null;
      }
    },

    /**
     * Write data to sessionStorage under the given key with the current timestamp.
     * Silently ignores storage quota errors (private browsing, full storage, etc.).
     *
     * @param {string} key - logical key (e.g. 'today-stats')
     * @param {any} data - serialisable payload
     */
    swrWrite(key, data) {
      try {
        sessionStorage.setItem('admin-dash-swr:' + key, JSON.stringify({ data, ts: Date.now() }));
      } catch (_e) {
        // Quota exceeded or private mode — ignore
      }
    },

    /**
     * Remove a specific SWR entry (e.g. on explicit user refresh).
     *
     * @param {string} key
     */
    swrInvalidate(key) {
      try {
        sessionStorage.removeItem('admin-dash-swr:' + key);
      } catch (_e) {
        /* ignore */
      }
    },

    // Request Queue System to prevent API throttling
    requestQueue: {
      maxConcurrent: 4, // Default limit; overridden at runtime by _getMaxConcurrent()
      maxQueueLength: 100, // Cap pending queue to prevent unbounded growth (100 waiting + active = max)
      queue: [],
      active: 0,

      // Reference to core for dynamic concurrency lookup (set during init).
      _core: null,

      /**
       * Return the effective concurrency cap for the current connection.
       * On slow connections (2g/slow-2g) we cap at MAX_CONCURRENT_SLOW (2) to prevent
       * head-of-line blocking; on all other connections MAX_CONCURRENT_NORMAL (4).
       * Falls back to this.maxConcurrent when core is unavailable.
       *
       * @returns {number}
       */
      _getMaxConcurrent() {
        if (!this._core) return this.maxConcurrent;
        const cfg = this._core.config;
        if (this._core.isSlowConnection()) {
          return cfg.MAX_CONCURRENT_SLOW != null ? cfg.MAX_CONCURRENT_SLOW : 2;
        }
        return cfg.MAX_CONCURRENT_NORMAL != null ? cfg.MAX_CONCURRENT_NORMAL : 4;
      },

      _dropOldestIfFull() {
        if (this.queue.length >= this.maxQueueLength) {
          const dropped = this.queue.pop();
          if (dropped) {
            dropped.reject(new Error('Request queue full — dropped oldest pending request'));
          }
        }
      },

      async enqueue(fetchFn, retries) {
        return new Promise((resolve, reject) => {
          this._dropOldestIfFull();
          this.queue.push({ fetchFn, resolve, reject, retries });
          this.process();
        });
      },

      /**
       * Enqueue urgent request at front of queue — used for user-initiated actions (refresh, manual fetch).
       * unshift() ensures immediate priority; processed before queued-background fetches when concurrency slot opens.
       */
      async enqueueUrgent(fetchFn, retries) {
        return new Promise((resolve, reject) => {
          this._dropOldestIfFull();
          this.queue.unshift({ fetchFn, resolve, reject, retries });
          this.process();
        });
      },

      async process() {
        const cap = this._getMaxConcurrent();
        if (this.active >= cap || this.queue.length === 0) {
          return;
        }

        const { fetchFn, resolve, reject, retries } = this.queue.shift();
        this.active++;

        try {
          const result = await this.retryWithBackoff(fetchFn, retries);
          resolve(result);
        } catch (error) {
          reject(error);
        } finally {
          this.active--;
          this.process(); // Process next in queue
        }
      },

      /**
       * Retry with exponential backoff — handles both HTTP 429 and GraphQL Throttled errors.
       * Exponential delay (800ms, 1600ms, 3200ms...) separates retries to avoid hammering.
       * Returns {response, data} on success; throws Error on final failure after maxRetries.
       */
      async retryWithBackoff(fetchFn, maxRetries = 2, baseDelay = 800) {
        let lastError;

        for (let attempt = 0; attempt < maxRetries; attempt++) {
          try {
            const response = await fetchFn();

            // Check if response is throttled
            if (response.ok) {
              const data = await response.json();

              // Check for GraphQL throttling errors
              if (data.errors?.some((e) => e.message?.includes('Throttled'))) {
                throw new Error('Throttled');
              }

              return { response, data };
            }

            // If 429 (Too Many Requests), retry
            if (response.status === 429) {
              throw new Error('Throttled');
            }

            // For other errors, return the response with safe JSON parsing
            let _errData = {};
            try {
              const _ct = response.headers.get('content-type') || '';
              if (_ct.includes('application/json')) {
                _errData = await response.json();
              } else {
                await response.text(); // drain body to avoid leak
              }
            } catch (_) {
              /* ignore parse errors on non-JSON error responses */
            }
            return { response, data: _errData };
          } catch (error) {
            lastError = error;

            if (attempt < maxRetries - 1) {
              const isThrottled = error.message === 'Throttled';
              const isNetwork = error instanceof TypeError; // "Failed to fetch" etc.

              if (isThrottled || isNetwork) {
                const delay = baseDelay * Math.pow(2, attempt);
                await new Promise((resolve) => {
                  setTimeout(resolve, delay);
                });
                continue;
              }
            }

            // Last attempt or non-retryable error — throw
            throw error;
          }
        }

        throw lastError;
      },
    },

    // Queued fetch helper to prevent throttling
    async queuedFetch(url, options = {}, urgent = false) {
      // Per-request timeout — adaptive based on connection quality.
      // 2g/slow-2g → REQUEST_TIMEOUT_SLOW_MS (60 s), 3g → REQUEST_TIMEOUT_3G_MS (30 s),
      // 4g/unknown → REQUEST_TIMEOUT_MS (15 s). Prevents hanging forever on slow pipes.
      const timeoutMs = this.getAdaptiveTimeoutMs();
      const timeoutController = new AbortController();
      // Armed when the request dequeues (in enqueueFn below), not now, so time spent
      // waiting for a free queue slot does not count against the per-request timeout.
      let timeoutId = null;

      // Combine timeout signal with any caller-supplied signal (view-level abort).
      let combinedSignal;
      const externalSignal = options.signal;
      if (externalSignal) {
        if (typeof AbortSignal.any === 'function') {
          combinedSignal = AbortSignal.any([timeoutController.signal, externalSignal]);
        } else {
          // Fallback: link external signal manually — abort our timeout controller when it fires.
          combinedSignal = timeoutController.signal;
          if (externalSignal.aborted) {
            timeoutController.abort();
          } else {
            externalSignal.addEventListener('abort', () => timeoutController.abort(), {
              once: true,
            });
          }
        }
      } else {
        combinedSignal = timeoutController.signal;
      }

      const fetchOptions = { ...options, signal: combinedSignal };

      try {
        const enqueueFn = urgent
          ? this.requestQueue.enqueueUrgent.bind(this.requestQueue)
          : this.requestQueue.enqueue.bind(this.requestQueue);
        const { response, data } = await enqueueFn(() => {
          if (timeoutMs > 0) {
            timeoutId = setTimeout(() => timeoutController.abort(), timeoutMs);
          }
          return fetch(url, fetchOptions);
        });
        return { response, data };
      } catch (err) {
        if (err.name === 'AbortError') {
          // Distinguish timeout (our controller aborted) from explicit view cancellation.
          if (timeoutController.signal.aborted && !(externalSignal && externalSignal.aborted)) {
            throw new (window.AdminDashboardTimeoutError || Error)(
              `Request timed out after ${timeoutMs}ms: ${url}`
            );
          }
          // Explicit cancellation (tab/period change) — rethrow as-is so callers can ignore it.
          throw err;
        }
        error('Queued fetch error:', err);
        throw err;
      } finally {
        if (timeoutId !== null) clearTimeout(timeoutId);
      }
    },

    // === DEFERRED DOM WRITES ===

    // Render callbacks waiting for the next animation frame.
    _pendingDomWrites: [],
    _domWriteFrameId: null,

    /**
     * Schedule a batch of DOM writes for the next animation frame.
     *
     * Drop-in replacement for a bare requestAnimationFrame() around card-render
     * writes. The browser never runs rAF callbacks while the document is hidden,
     * so a render scheduled from a fetch that resolved on a backgrounded tab was
     * stranded indefinitely: the promise resolved, no error path ran, and the card
     * kept its loading skeleton forever with nothing left to retry it. When the
     * document is hidden there is no rendering to batch against, so the writes run
     * immediately instead.
     */
    scheduleDomWrite(callback) {
      if (typeof callback !== 'function') return;
      this._pendingDomWrites.push(callback);

      if (typeof document !== 'undefined' && document.visibilityState === 'hidden') {
        this.flushDomWrites();
        return;
      }

      if (this._domWriteFrameId === null) {
        this._domWriteFrameId = requestAnimationFrame(() => {
          this._domWriteFrameId = null;
          this.flushDomWrites();
        });
      }
    },

    /**
     * Run every queued DOM write now and cancel any frame still pending.
     * Also called on visibilitychange(hidden) so writes scheduled while the page
     * was visible are not stranded by the tab being backgrounded before the frame.
     */
    flushDomWrites() {
      if (this._domWriteFrameId !== null) {
        cancelAnimationFrame(this._domWriteFrameId);
        this._domWriteFrameId = null;
      }
      // Swap before running: a callback may schedule further writes.
      const pending = this._pendingDomWrites;
      this._pendingDomWrites = [];
      pending.forEach((fn) => {
        try {
          fn();
        } catch (err) {
          this.logError('[Core] deferred DOM write failed:', err);
        }
      });
    },

    // === UTILITY FUNCTIONS ===

    // Safari-safe date parsing helper
    // Safari requires ISO 8601 format; this handles null/invalid dates gracefully
    safeDateParse(dateInput) {
      if (!dateInput) return null;
      const date = new Date(dateInput);
      return isNaN(date.getTime()) ? null : date;
    },

    /**
     * Format number with commas and optional decimal places.
     *
     * @param {number|string|null|undefined} num - Value to format
     * @param {number} [decimals=2] - Decimal places (must be a non-negative integer)
     * @returns {string} Formatted number string, or '0' for invalid input
     */
    formatNumber(num, decimals = 2) {
      const parsed = parseFloat(num);
      if (isNaN(parsed)) return '0';
      const d = Number.isFinite(decimals) && decimals >= 0 ? Math.floor(decimals) : 2;
      return parsed.toLocaleString('en-US', {
        minimumFractionDigits: d,
        maximumFractionDigits: d,
      });
    },

    /**
     * Format value as a USD currency string.
     *
     * @param {number|string|null|undefined} num - Value to format
     * @param {number} [decimals=2] - Decimal places
     * @returns {string} Currency string like '$1,234.56'
     */
    formatCurrency(num, decimals = 2) {
      return `$${this.formatNumber(num, decimals)}`;
    },

    // Format date as "12 Oct 2005" (day month year)
    formatDate(dateInput) {
      if (!dateInput) return '';
      const date = this.safeDateParse(dateInput);
      if (!date) return '';

      const day = date.getUTCDate();
      const month = date.toLocaleDateString('en-US', { month: 'short', timeZone: 'UTC' });
      const year = date.getUTCFullYear();

      return `${day} ${month} ${year}`;
    },

    /**
     * Escape HTML to prevent XSS.
     * Accepts any type and coerces to string before escaping, so passing
     * numbers, booleans, or objects does not silently return '' (the old
     * falsy-check behaviour would swallow 0, false, and empty string).
     *
     * @param {*} text - Value to escape (coerced to string)
     * @returns {string} HTML-escaped string
     */
    escapeHtml(text) {
      // Coerce to string first so 0, false, NaN, etc. are escaped rather than
      // silently swallowed by the old `if (!text) return ''` falsy guard.
      if (text == null) return '';
      const str = typeof text === 'string' ? text : String(text);
      if (!str) return '';
      if (!this._escapeEl) this._escapeEl = document.createElement('div');
      this._escapeEl.textContent = str;
      // Quotes too: the round-trip leaves them intact, allowing breakout from
      // attr="${escapeHtml(v)}". Do not re-escape quotes at call sites.
      return this._escapeEl.innerHTML.replace(/"/g, '&quot;').replace(/'/g, '&#39;');
    },

    /**
     * Build the visitor-location tooltip as DOM nodes from a plain data object.
     *
     * The tooltip payload travels in a data attribute as JSON. It must not
     * travel as markup: getAttribute decodes HTML entities, so any escaping
     * applied on the way in is undone on the way out and the string is
     * re-parsed as live HTML. Building nodes here means labels only ever reach
     * the DOM through textContent, which cannot produce an element.
     *
     * @param {{userAgents?: Array, bots?: Array}} data - Parsed tooltip payload
     * @returns {HTMLElement} A .location-tooltip element
     */
    buildLocationTooltip(data) {
      const root = document.createElement('div');
      root.className = 'location-tooltip';
      if (!data || typeof data !== 'object') return root;

      const addSection = (heading, items, spaced) => {
        if (!Array.isArray(items) || items.length === 0) return;
        const title = document.createElement('h4');
        title.textContent = heading;
        if (spaced) title.style.marginTop = '10px';
        root.appendChild(title);

        const list = document.createElement('ul');
        items.forEach((entry) => {
          if (!entry) return;
          const li = document.createElement('li');
          const prefix = entry.emoji ? `${entry.emoji} ` : '';
          li.appendChild(
            document.createTextNode(`${prefix}${entry.label == null ? '' : entry.label}: `)
          );
          const count = document.createElement('strong');
          count.textContent = String(parseInt(entry.count, 10) || 0);
          li.appendChild(count);
          list.appendChild(li);
        });
        root.appendChild(list);
      };

      addSection('User Agents:', data.userAgents, false);
      addSection('Bots:', data.bots, true);
      return root;
    },

    /**
     * Validate a URL is safe to open (https/http only — blocks javascript: and data: protocols).
     * Returns the URL unchanged if safe, or null if the protocol is not allowed.
     * @param {string} url
     * @returns {string|null}
     */
    safeUrl(url) {
      if (!url || typeof url !== 'string') return null;
      const trimmed = url.trim();
      // Allow only http and https — reject javascript:, data:, vbscript:, etc.
      if (!/^https?:\/\//i.test(trimmed)) return null;
      return trimmed;
    },

    // Calculate days between two dates
    calculateDaysBetween(dateString) {
      if (!dateString) return null;
      try {
        const date = this.safeDateParse(dateString);
        if (!date) return null;
        const now = new Date();
        const diffMs = now - date;
        const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));
        return diffDays >= 0 ? diffDays : null;
      } catch (_e) {
        return null;
      }
    },

    // Format order timing information for display after order count
    // Returns: "<span style='color: rgba(255,255,255,0.5); font-weight: normal; font-style: italic;'>(5d / 480d)</span>"
    formatOrderTiming(orderCount, firstOrderDate, lastOrderDate) {
      // Only show timing for repeat customers (orderCount > 1)
      if (!orderCount || parseInt(orderCount, 10) <= 1) {
        return '';
      }

      // Calculate days since last and first orders
      const daysSinceLast = lastOrderDate ? this.calculateDaysBetween(lastOrderDate) : null;
      const daysSinceFirst = firstOrderDate ? this.calculateDaysBetween(firstOrderDate) : null;

      // Only show timing if we have both dates
      if (daysSinceLast !== null && daysSinceFirst !== null) {
        return ` <span style="color: rgba(255,255,255,0.5); font-weight: normal; font-style: italic;">(${daysSinceLast}d / ${daysSinceFirst}d)</span>`;
      }

      return '';
    },

    // Order value tier thresholds (dollars) for activity-list color coding.
    // High/medium/low bucket an order's total into a CSS class + label pair,
    // shared by every activity list that renders order value badges.
    ORDER_VALUE_TIER_HIGH: 500,
    ORDER_VALUE_TIER_MEDIUM: 300,
    ORDER_VALUE_TIER_LOW: 100,

    // Classify an order's dollar total into a value tier for color coding.
    // Returns { valueTier, valueClass }, both '' when below the low threshold.
    getOrderValueTier(orderValue) {
      const value = parseFloat(orderValue) || 0;

      if (value >= this.ORDER_VALUE_TIER_HIGH) {
        return { valueTier: 'high-value', valueClass: 'value-red' };
      }
      if (value >= this.ORDER_VALUE_TIER_MEDIUM) {
        return { valueTier: 'medium-value', valueClass: 'value-yellow' };
      }
      if (value >= this.ORDER_VALUE_TIER_LOW) {
        return { valueTier: 'low-value', valueClass: 'value-green' };
      }
      return { valueTier: '', valueClass: '' };
    },

    // Categorize traffic source from referring site URL
    // Updated 2025-10-23: Distinguish Google Ads (paid) from Organic Search
    categorizeTrafficSource(referringSite) {
      if (!referringSite) {
        return { source: 'Direct', icon: '🔗' };
      }

      const url = referringSite.toLowerCase();

      // Check for newsletter/email campaign indicators (Klaviyo, email platforms, or UTM params)
      // Klaviyo campaign IDs are 18-digit numbers in utm_campaign parameter
      const klaviyoCampaignMatch = url.match(/utm_campaign=(\d{18})/);
      if (
        url.includes('utm_source=newsletter') ||
        url.includes('utm_source=klaviyo') ||
        url.includes('utm_medium=email') ||
        url.includes('a.klaviyo.com') ||
        url.includes('email_id=') ||
        url.includes('ctrk.klclick') || // Klaviyo click tracking domain
        url.includes('_kx=') || // Klaviyo tracking parameter in URL
        klaviyoCampaignMatch || // Klaviyo 18-digit campaign ID
        (url.includes('email') &&
          !url.includes('gmail') &&
          !url.includes('yahoo') &&
          !url.includes('outlook'))
      ) {
        return { source: 'Newsletter', icon: '📧' };
      } else if (url.includes('google')) {
        // Distinguish between Google Ads (paid) and Organic Search
        // Google Ads indicators: gclid, utm_source=google with utm_medium=cpc/ppc/paid
        const isGoogleAds =
          url.includes('gclid=') ||
          (url.includes('utm_source=google') &&
            (url.includes('utm_medium=cpc') ||
              url.includes('utm_medium=ppc') ||
              url.includes('utm_medium=paid')));

        if (isGoogleAds) {
          return { source: 'Google Ads', icon: '💰' };
        } else {
          // Organic Google search - don't attribute as "Google" traffic source
          return { source: 'Organic Search', icon: '🔍' };
        }
      } else if (url.includes('facebook') || url.includes('fb.')) {
        return { source: 'Facebook', icon: '👤' };
      } else if (url.includes('instagram')) {
        return { source: 'Instagram', icon: '📸' };
      } else if (url.includes('pinterest')) {
        return { source: 'Pinterest', icon: '📌' };
      } else if (url.includes('tiktok')) {
        return { source: 'TikTok', icon: '🎵' };
      } else if (url.includes('twitter') || url.includes('t.co') || url.includes('x.com')) {
        return { source: 'Twitter/X', icon: '🐦' };
      } else if (url.includes('youtube') || url.includes('youtu.be')) {
        return { source: 'YouTube', icon: '▶️' };
      } else if (url.includes('linkedin')) {
        return { source: 'LinkedIn', icon: '💼' };
      } else if (url.includes('reddit')) {
        return { source: 'Reddit', icon: '🤖' };
      } else if (url.includes('bing')) {
        return { source: 'Bing', icon: '🔍' };
      } else if (url.includes('yahoo')) {
        return { source: 'Yahoo', icon: '🔍' };
      } else {
        return { source: 'Referral', icon: '🌐' };
      }
    },

    // Resolve the traffic-source detail-panel display (icon + label) for an order.
    // Single source of truth for the Live Activity detail views: the desktop hover
    // tooltip (admin-dashboard.js) and the mobile tap drawer (admin-dashboard-mobile.js).
    // Precedence mirrors the backend attribution pipeline in shopify-proxy.js:
    //   1. server-computed trafficSourceDetail (e.g. "Klaviyo: Summer Sale Email")
    //   2. server-computed trafficSource badge (plus abbreviated blogTitle for Blog)
    //   3. raw referring-URL categorization as a graceful fallback when no badge exists
    // Returns plain text; callers are responsible for HTML-escaping the label.
    resolveOrderTrafficSource(order) {
      const o = order || {};
      const badge = o.trafficSource;

      let icon = '🌐';
      let display = 'Direct / Unknown';

      if (badge && badge !== 'Direct') {
        display = badge;
        icon = this._trafficSourceIcon(badge);
      } else if (badge === 'Direct') {
        display = 'Direct';
      } else {
        // No server badge: degrade gracefully by categorizing the raw referring URL.
        const referrer = o.referringSite || o.landingSite || o.sourceName || null;
        if (referrer) {
          const categorized = this.categorizeTrafficSource(referrer);
          icon = categorized.icon;
          display =
            categorized.source === 'Referral'
              ? `Referral: ${referrer.replace(/^https?:\/\//, '')}`
              : categorized.source;
        }
      }

      // Prefer the detailed server label; otherwise append an abbreviated Blog title.
      let label = display;
      if (o.trafficSourceDetail && o.trafficSourceDetail !== display) {
        label = o.trafficSourceDetail;
      } else if (badge === 'Blog' && o.blogTitle) {
        const abbreviated =
          o.blogTitle.length > 40 ? o.blogTitle.substring(0, 37) + '...' : o.blogTitle;
        label = `${display}: ${abbreviated}`;
      }

      return { icon, label };
    },

    // Map a server-computed trafficSource badge to its display icon.
    // Kept in sync with the desktop tooltip's icon precedence so both surfaces match.
    _trafficSourceIcon(badge) {
      const source = String(badge).toLowerCase();
      if (source.includes('klaviyo') || source.includes('email')) return '📧';
      if (source.includes('instagram') || source.includes('facebook')) return '📱';
      if (source.includes('google')) return '🔍';
      if (source.includes('microsoft') || source.includes('bing')) return '🔷';
      if (source.includes('ai:') || source.includes('chatgpt') || source.includes('claude')) {
        return '🤖';
      }
      if (source.includes('pinterest')) return '📌';
      if (source.includes('tiktok')) return '🎵';
      if (source === 'shop') return '🛍️';
      return '🌐';
    },

    // Build the compact traffic-source pill for a Live Activity order ROW (not the detail
    // panel). Routes through resolveOrderTrafficSource so rows get the same 3-tier fallback the
    // popup already has - this is what makes referral badges appear on orders that carry no
    // server-set trafficSource. Returns '' for direct/unknown (no pill); otherwise a ' • <span>'
    // fragment with the label HTML-escaped and length-capped for the single-line row.
    trafficSourceRowBadge(order) {
      const resolved = this.resolveOrderTrafficSource(order || {});
      const label = resolved && resolved.label;
      if (!label || label === 'Direct' || label === 'Direct / Unknown') return '';
      const compact = label.length > 24 ? label.substring(0, 21).trim() + '...' : label;
      return ` • <span class="traffic-source-badge">${this.escapeHtml(compact)}</span>`;
    },

    // === WEB VITALS TRACKING ===

    // Initialize Web Vitals tracking (Core Web Vitals)
    initWebVitals() {
      this._boundUpdateVitalDisplay = this._updateVitalDisplay.bind(this);
      // Track LCP (Largest Contentful Paint)
      try {
        const lcpObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          if (entries.length > 0) {
            const lastEntry = entries[entries.length - 1];
            this.state.webVitals.lcp = lastEntry.renderTime || lastEntry.loadTime;
            debug('[WebVitals] LCP:', this.state.webVitals.lcp.toFixed(0) + 'ms');
            this.updateWebVitalsDisplay();
          }
        });
        lcpObserver.observe({ type: 'largest-contentful-paint', buffered: true });
      } catch (_e) {
        warn('LCP tracking not supported');
      }

      // Track FID (First Input Delay)
      try {
        const fidObserver = new PerformanceObserver((list) => {
          const entries = list.getEntries();
          if (!entries.length) return;
          const lastEntry = entries[entries.length - 1];
          this.state.webVitals.fid = lastEntry.processingStart - lastEntry.startTime;
          debug('[WebVitals] FID:', this.state.webVitals.fid.toFixed(0) + 'ms');
          this.updateWebVitalsDisplay();
        });
        fidObserver.observe({ type: 'first-input', buffered: true });
      } catch (_e) {
        warn('FID tracking not supported');
      }

      // Track CLS (Cumulative Layout Shift)
      try {
        let clsValue = 0;
        const clsObserver = new PerformanceObserver((list) => {
          let changed = false;
          for (const entry of list.getEntries()) {
            if (!entry.hadRecentInput) {
              clsValue += entry.value;
              changed = true;
            }
          }
          if (changed) {
            this.state.webVitals.cls = clsValue;
            debug('[WebVitals] CLS:', this.state.webVitals.cls.toFixed(4));
            this.updateWebVitalsDisplay();
          }
        });
        clsObserver.observe({ type: 'layout-shift', buffered: true });
      } catch (_e) {
        warn('CLS tracking not supported');
      }

      // Track TTFB (Time to First Byte)
      try {
        const navigationEntry = performance.getEntriesByType('navigation')[0];
        if (navigationEntry) {
          this.state.webVitals.ttfb = navigationEntry.responseStart - navigationEntry.requestStart;
          debug('[WebVitals] TTFB:', this.state.webVitals.ttfb.toFixed(0) + 'ms');
          this.updateWebVitalsDisplay();
        }
      } catch (_e) {
        warn('TTFB tracking not supported');
      }
    },

    // Update a single vital's value and benchmark elements
    _updateVitalDisplay(value, config, labels, valueId, benchmarkId, formatFn) {
      const el = document.getElementById(valueId);
      if (el) {
        el.textContent = formatFn(value, config);
        el.className = 'metric-value';
        if (value < config.thresholds.good) {
          el.classList.add('metric-status-good');
        } else if (value < config.thresholds.needsImprovement) {
          el.classList.add('metric-status-needs-improvement');
        } else {
          el.classList.add('metric-status-poor');
        }
      }
      const benchmarkEl = document.getElementById(benchmarkId);
      if (benchmarkEl) {
        const comparison =
          value < config.thresholds.good
            ? labels.excellent
            : value < config.thresholds.needsImprovement
              ? labels.needsImprovement
              : labels.poor;
        const cssClass =
          value < config.thresholds.good
            ? 'good'
            : value < config.thresholds.needsImprovement
              ? 'needs-improvement'
              : 'poor';
        const span = document.createElement('span');
        span.className = `benchmark-${cssClass}`;
        span.textContent = comparison;
        benchmarkEl.replaceChildren(
          span,
          document.createTextNode(` | Industry avg: ${config.industryAvg}`)
        );
      }
    },

    _showVitalAwaitingInput(valueId, benchmarkId) {
      const el = document.getElementById(valueId);
      if (el) {
        el.textContent = 'Awaiting input';
        el.className = 'metric-value';
      }
      const benchmarkEl = document.getElementById(benchmarkId);
      if (benchmarkEl) {
        benchmarkEl.textContent = 'Measured on your first click or keypress';
      }
    },

    // Update Web Vitals display with color-coded status
    updateWebVitalsDisplay() {
      if (!this._boundUpdateVitalDisplay) return;
      const vitals = this.state.webVitals;
      const metrics = this.config.performanceMetrics;
      const { labels } = metrics;
      const updateVital = this._boundUpdateVitalDisplay;

      if (vitals.lcp !== null) {
        updateVital(
          vitals.lcp,
          metrics.lcp,
          labels,
          'lcp-value',
          'lcp-benchmark',
          (v, c) => `${(v / c.divisor).toFixed(2)}${c.unit}`
        );
      }
      if (vitals.fid !== null) {
        updateVital(
          vitals.fid,
          metrics.fid,
          labels,
          'fid-value',
          'fid-benchmark',
          (v, c) => `${v.toFixed(0)}${c.unit}`
        );
      } else {
        // first-input never fires for a session that only reads the dashboard, so without
        // this the FID tile keeps its loading shimmer for the life of the page.
        this._showVitalAwaitingInput('fid-value', 'fid-benchmark');
      }
      if (vitals.cls !== null) {
        updateVital(vitals.cls, metrics.cls, labels, 'cls-value', 'cls-benchmark', (v) =>
          v.toFixed(3)
        );
      }
      if (vitals.ttfb !== null) {
        updateVital(
          vitals.ttfb,
          metrics.ttfb,
          labels,
          'ttfb-value',
          'ttfb-benchmark',
          (v, c) => `${v.toFixed(0)}${c.unit}`
        );
      }

      this.updateCWVScore();
    },

    // Calculate Core Web Vitals score (percentage-based)
    updateCWVScore() {
      const vitals = this.state.webVitals;
      const { lcp, fid, cls, ttfb } = this.config.performanceMetrics;
      let score = 0;
      let count = 0;

      // LCP scoring (Good: 100, Needs Improvement: 50, Poor: 0)
      if (vitals.lcp !== null) {
        if (vitals.lcp < lcp.thresholds.good) score += 100;
        else if (vitals.lcp < lcp.thresholds.needsImprovement) score += 50;
        count++;
      }

      // FID scoring
      if (vitals.fid !== null) {
        if (vitals.fid < fid.thresholds.good) score += 100;
        else if (vitals.fid < fid.thresholds.needsImprovement) score += 50;
        count++;
      }

      // CLS scoring
      if (vitals.cls !== null) {
        if (vitals.cls < cls.thresholds.good) score += 100;
        else if (vitals.cls < cls.thresholds.needsImprovement) score += 50;
        count++;
      }

      // TTFB scoring
      if (vitals.ttfb !== null) {
        if (vitals.ttfb < ttfb.thresholds.good) score += 100;
        else if (vitals.ttfb < ttfb.thresholds.needsImprovement) score += 50;
        count++;
      }

      if (count > 0) {
        const avgScore = score / count;
        let gradeClass = 'performance-grade-f';

        if (avgScore >= 90) {
          gradeClass = 'performance-grade-a';
        } else if (avgScore >= 80) {
          gradeClass = 'performance-grade-b';
        } else if (avgScore >= 70) {
          gradeClass = 'performance-grade-c';
        } else if (avgScore >= 60) {
          gradeClass = 'performance-grade-d';
        }

        const cwvEl = document.getElementById('cwv-score');
        if (cwvEl) {
          cwvEl.textContent = `${avgScore.toFixed(0)}%`;
          cwvEl.className = `metric-value ${gradeClass}`;
        }

        const benchmarkEl = document.getElementById('cwv-benchmark');
        if (benchmarkEl) {
          const industryComparison =
            avgScore >= 75 ? 'Above average' : avgScore >= 50 ? 'Average' : 'Below average';
          const span = document.createElement('span');
          span.className = `benchmark-${industryComparison.toLowerCase().replace(' ', '-')}`;
          span.textContent = industryComparison;
          const textNode = document.createTextNode(` (${avgScore.toFixed(0)}%)`);
          benchmarkEl.replaceChildren(span, textNode);
        }
      }
    },

    // === IDLE DETECTION & STATE MANAGEMENT ===

    // Setup idle detection for auto-refresh
    setupIdleDetection() {
      // Prevent duplicate listener registration
      if (this.state.idleListenersAdded) return;
      this.state.idleListenersAdded = true;

      const resetIdleTimer = () => {
        // Deregister the old timer before clearing so the registry stays consistent
        if (this.state.idleTimer != null) {
          this._timerRegistry.delete(this.state.idleTimer);
          clearTimeout(this.state.idleTimer);
        }

        // Update last activity time
        this.state.lastActivityTime = Date.now();

        // Set new timer — re-fetch data after idle timeout without losing tab state
        const timerId = setTimeout(() => {
          debug(
            '[Idle] Idle timeout fired — refreshing data after',
            (this.config.idleTimeout / 60000).toFixed(0) + 'min of inactivity'
          );
          if (window.AdminDashboard && typeof window.AdminDashboard.loadData === 'function') {
            window.AdminDashboard.loadData();
          } else {
            location.reload(true);
          }
        }, this.config.idleTimeout);
        this.state.idleTimer = this.registerTimer(timerId, 'timeout');
      };

      // Store handler for cleanup
      this.state.resetIdleTimerHandler = resetIdleTimer;

      // High-frequency events (mousemove, scroll) use a RAF gate so rapid touch/scroll
      // events don't each queue a fresh clearTimeout+setTimeout pair.
      // One idle-timer reset per animation frame is sufficient for 30-min idle detection.
      let _idleRafPending = false;
      const resetIdleTimerThrottled = () => {
        if (_idleRafPending) return;
        _idleRafPending = true;
        requestAnimationFrame(() => {
          _idleRafPending = false;
          resetIdleTimer();
        });
      };
      this.state.resetIdleTimerThrottledHandler = resetIdleTimerThrottled;

      // High-frequency events use the RAF gate; low-frequency events fire directly.
      const highFreqEvents = ['mousemove', 'scroll'];
      const lowFreqEvents = ['keydown', 'click', 'touchstart'];
      highFreqEvents.forEach((event) => {
        document.addEventListener(event, resetIdleTimerThrottled, { passive: true });
      });
      lowFreqEvents.forEach((event) => {
        document.addEventListener(event, resetIdleTimer, { passive: true });
      });

      // Start the initial timer
      resetIdleTimer();
    },

    // Cleanup idle detection listeners and cancel the idle timer.
    // Safe to call multiple times — guarded by idleListenersAdded flag.
    cleanupIdleDetection() {
      if (!this.state.idleListenersAdded) return;

      const highFreqEvents = ['mousemove', 'scroll'];
      const lowFreqEvents = ['keydown', 'click', 'touchstart'];
      if (this.state.resetIdleTimerThrottledHandler) {
        highFreqEvents.forEach((event) => {
          document.removeEventListener(event, this.state.resetIdleTimerThrottledHandler);
        });
        this.state.resetIdleTimerThrottledHandler = null;
      }
      lowFreqEvents.forEach((event) => {
        document.removeEventListener(event, this.state.resetIdleTimerHandler);
      });

      if (this.state.idleTimer != null) {
        this._timerRegistry.delete(this.state.idleTimer);
        clearTimeout(this.state.idleTimer);
        this.state.idleTimer = null;
      }

      this.state.idleListenersAdded = false;
      this.state.resetIdleTimerHandler = null;
    },

    /**
     * Register page-lifecycle handling: PAUSE while the tab is hidden, TEARDOWN when the
     * page is actually going away.
     *
     * The distinction is load-bearing. A hidden tab is not a dead tab — it comes back.
     * Running the full teardown on visibilitychange(hidden) is what caused the dashboard's
     * long-standing "shows 0 / Setup Required until you refresh" bug:
     *   1. removeAllListeners() wiped the centralized listener registry, which is where
     *      setupStaleDataRefresh() registers its visibilitychange/focus/pageshow recovery
     *      handlers — so nothing could ever reload the data again.
     *   2. cancelAllViews() aborted the in-flight cold-load requests, and every abort lands
     *      in a catch block that paints a TERMINAL empty state ("Setup Required",
     *      "Deploy proxy to see real activity", "0 events").
     *   3. clearAllTimers() killed auto-refresh and destroyAllCharts() dropped the charts,
     *      with no code path anywhere that re-created either.
     * Glancing at another tab for one second during the ~4s staggered cold load was enough
     * to leave the dashboard permanently dead until a manual reload.
     *
     * Events:
     *  - visibilitychange (hidden)  -> pause():   reversible work only (idle detection).
     *  - visibilitychange (visible) -> resume():  re-arm whatever pause() stopped.
     *  - beforeunload / pagehide    -> teardown(): the page is leaving; destroy everything.
     *
     * Safe to call multiple times — the _lifecycleCleanupRegistered guard prevents
     * duplicate listener registration.
     */
    setupPageLifecycleCleanup() {
      if (this._lifecycleCleanupRegistered) return;
      this._lifecycleCleanupRegistered = true;

      const teardown = () => {
        this.cleanupIdleDetection();
        this.clearAllTimers();
        this.removeAllListeners();
        // Abort all in-flight fetch requests so pending network activity does
        // not resolve after the page is unloaded.
        window.AdminDashboardAPI?.cancelAllViews?.();
        // Destroy Chart.js instances from both registries so canvas references
        // and animation frames are released and do not leak across navigation.
        window.AdminDashboardCharts?.destroyAllCharts?.();
        window.AdminDashboardAdsCharts?.destroyAll?.();
        // Clean up Linnworks timers — these are registered in the core timer
        // registry (via init()) so clearAllTimers() handles them, but calling
        // cleanup() also nullifies the local timer refs for idempotent safety.
        window.AdminDashboardLinnworks?.cleanup?.();
      };
      this._lifecycleTeardown = teardown;

      // Pause: stop only work that resume() can put back. Timers keep running (the
      // pollers that matter already self-suppress on a hidden tab), listeners stay
      // registered, in-flight requests are allowed to finish, charts stay alive.
      const pause = () => {
        if (this._lifecyclePaused) return;
        this._lifecyclePaused = true;
        this.cleanupIdleDetection();
      };

      const resume = () => {
        if (!this._lifecyclePaused) return;
        this._lifecyclePaused = false;
        this._resumeHandlers.forEach((fn) => {
          try {
            fn();
          } catch (err) {
            this.logError('[Core] resume handler failed:', err);
          }
        });
      };

      document.addEventListener('visibilitychange', () => {
        if (document.visibilityState === 'hidden') {
          // Run any write whose frame will now never arrive.
          this.flushDomWrites();
          pause();
        } else resume();
      });

      window.addEventListener('beforeunload', teardown);
      // pagehide is the reliable terminal event on iOS Safari, where beforeunload
      // is unreliable. visibilitychange(hidden) no longer tears down, so without
      // this the mobile teardown path would be lost.
      window.addEventListener('pagehide', teardown);
    },

    /**
     * Register a callback to run when the tab becomes visible again after being hidden.
     * Used by AdminDashboard to re-arm idle detection and to recover a dashboard whose
     * data never loaded. Returns the handler for symmetry with registerListener().
     */
    onResume(fn) {
      if (typeof fn === 'function') this._resumeHandlers.push(fn);
      return fn;
    },

    /**
     * Debug helper — returns a snapshot of all registered lifecycle resources.
     * Accessible via window.__adminDash?.lifecycle?.status() in DevTools.
     *
     * Returns:
     *   timerCount  — number of timers currently in the registry
     *   timers      — array of {id, type} for each registered timer
     *   listenerCount — number of event listeners currently in the registry
     *   listeners   — array of {target, event} for each registered listener
     *   chartCount  — number of Chart.js instances (core + ads combined)
     *   linnworksTimers — {dataRefresh, trackingRefresh} raw timer ids from Linnworks
     *   idleTimer   — current idle timer id (null if none)
     *   cleanupRegistered — whether lifecycle cleanup listeners are installed
     */
    lifecycleStatus() {
      const timerEntries = [];
      this._timerRegistry.forEach((type, id) => timerEntries.push({ id, type }));

      const listenerEntries = this._listenerRegistry.map(({ target, event }) => ({
        target: target === window ? 'window' : target === document ? 'document' : String(target),
        event,
      }));

      const coreChartCount = Object.keys(window.AdminDashboardCharts?._charts ?? {}).length;
      const adsChartCount = window.AdminDashboardAdsCharts?._charts?.size ?? 0;

      const linnworks = window.AdminDashboardLinnworks?.timers ?? null;

      return {
        timerCount: this._timerRegistry.size,
        timers: timerEntries,
        listenerCount: this._listenerRegistry.length,
        listeners: listenerEntries,
        chartCount: coreChartCount + adsChartCount,
        linnworksTimers: linnworks
          ? {
              dataRefresh: linnworks.dataRefresh,
              trackingRefresh: linnworks.trackingRefresh,
            }
          : null,
        idleTimer: this.state.idleTimer,
        cleanupRegistered: this._lifecycleCleanupRegistered,
      };
    },

    // Update last update time display
    updateLastUpdateTime() {
      const now = new Date();
      const timeString = now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
      });
      const lastUpdateEl = document.getElementById('last-update-time');
      if (lastUpdateEl) {
        lastUpdateEl.textContent = timeString;
      }
    },

    // ---------------------------------------------------------------------------
    // Connection health API
    // ---------------------------------------------------------------------------

    /**
     * Record a TimeoutError from any fetch call.
     * Increments the consecutive-timeout counter and, once the threshold is
     * reached, switches to connection-problem mode (manual-refresh-only).
     * Returns true if we just crossed the threshold.
     */
    recordTimeoutError() {
      const health = this.state.connectionHealth;
      health.consecutiveTimeouts += 1;
      if (
        !health.connectionProblem &&
        health.consecutiveTimeouts >= this.config.CONNECTION_HEALTH_THRESHOLD
      ) {
        health.connectionProblem = true;
        health.autoRefreshPaused = true;
        debug('[ConnectionHealth] 3 consecutive timeouts — switching to manual-refresh-only mode');
        return true; // just crossed threshold
      }
      return false;
    },

    /**
     * Reset the connection health counters after a successful request.
     * Clears connectionProblem and autoRefreshPaused (unless offline).
     * Returns true if we were previously in problem mode (callers may want to
     * dismiss the warning banner).
     */
    resetConnectionHealth() {
      const health = this.state.connectionHealth;
      const wasProblem = health.connectionProblem;
      health.consecutiveTimeouts = 0;
      health.connectionProblem = false;
      if (!health.isOffline) {
        health.autoRefreshPaused = false;
      }
      return wasProblem;
    },

    /**
     * Returns the effective connection type string from the Network Information
     * API, or 'unknown' when not supported.
     * Possible values: 'slow-2g' | '2g' | '3g' | '4g' | 'unknown'
     */
    getEffectiveConnectionType() {
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      if (conn && conn.effectiveType) {
        return conn.effectiveType;
      }
      return 'unknown';
    },

    /**
     * Returns true when the Network Information API reports a very slow
     * connection ('slow-2g' or '2g').  False for 3g, 4g, unknown, or when
     * the API is unavailable (we don't want to incorrectly throttle desktops).
     */
    isSlowConnection() {
      const et = this.getEffectiveConnectionType();
      return et === '2g' || et === 'slow-2g';
    },

    /**
     * Return the appropriate fetch timeout for the current connection.
     * Three tiers based on Network Information API effectiveType:
     *   2g / slow-2g → REQUEST_TIMEOUT_SLOW_MS  (60 s) — very constrained pipe
     *   3g            → REQUEST_TIMEOUT_3G_MS    (30 s) — moderate constraint
     *   4g / unknown  → REQUEST_TIMEOUT_MS       (15 s) — normal / no API
     */
    getAdaptiveTimeoutMs() {
      const et = this.getEffectiveConnectionType();
      if (et === '2g' || et === 'slow-2g') return this.config.REQUEST_TIMEOUT_SLOW_MS;
      if (et === '3g') return this.config.REQUEST_TIMEOUT_3G_MS;
      return this.config.REQUEST_TIMEOUT_MS;
    },

    // Check if data is stale (older than 20 minutes)
    isDataStale() {
      // A load that is currently running is not stale, it is simply not finished yet.
      // Without this guard the "never loaded => stale" rule below would let a focus or
      // visibilitychange event fired during the initial load kick off a duplicate loadData().
      if (this.state.dataLoadInFlight) return false;
      if (!this.state.lastDataFetchTimestamp) {
        // Never successfully loaded is the MOST stale state possible, not the least.
        // This used to return false, which is what pinned an interrupted cold load at
        // "0"/"Setup Required"/"Deploy proxy" forever: every recovery path
        // (visibilitychange, focus, pageshow in setupStaleDataRefresh) gates on
        // isDataStale(), so none of them ever re-fired and only a manual reload recovered.
        return true;
      }
      const now = Date.now();
      const ageInMinutes = (now - this.state.lastDataFetchTimestamp) / (1000 * 60);
      return ageInMinutes > 20;
    },

    /**
     * Consolidated error handler: logs in dev mode only.
     * Reduces scattered error-handling try/catch blocks.
     *
     * @param {string} context - Dev-mode log prefix (e.g., '[Admin] loadRevenueVelocity')
     * @param {Error} err - Error object to log
     */
    logError(context, err) {
      if (isDev) error(context, err);
    },

    /**
     * Wrap an async operation with loading UI: show loading -> execute -> hide/show result.
     * Standardizes the "showLoading -> fetch -> catch -> show error -> clearLoading" pattern.
     *
     * @param {string|HTMLElement} container - Container id or element
     * @param {Function} asyncFn - Async function that returns fetched data
     * @param {string} [errorMessage='Failed to load'] - Error message if asyncFn throws
     * @returns {Promise} Resolves to the result of asyncFn, or rejects if UI unavailable
     */
    async withLoading(container, asyncFn, errorMessage = 'Failed to load') {
      if (!window.AdminDashboardUI) return asyncFn();
      const ui = window.AdminDashboardUI;
      ui.showLoading(container);
      try {
        const result = await asyncFn();
        ui.clearLoading(container);
        return result;
      } catch (err) {
        ui.showError(container, errorMessage);
        throw err;
      }
    },
  };

  // Wire the requestQueue back to core so _getMaxConcurrent() can call isSlowConnection().
  // Done after the object literal so the self-reference is valid.
  window.AdminDashboardCore.requestQueue._core = window.AdminDashboardCore;

  // Expose isDev flag so sibling modules can read it instead of re-declaring the same
  // hostname check. Assigned after the literal to keep the IIFE-scope const in place.
  window.AdminDashboardCore.isDev = isDev;

  // Debug helper namespace — available in DevTools as window.__adminDash.lifecycle.status()
  // Only the status() call is exposed; writing internal state is not permitted through this path.
  window.__adminDash = {
    lifecycle: {
      status: () => window.AdminDashboardCore.lifecycleStatus(),
    },
  };
})();
